/**
 * 論文用テキスト（Methods / Results）の言語切替。
 * UI 言語とは独立し、既定は英語（en）。
 */

import type { AnalysisOptions, ResultCache } from '../stores/systems/keisuke/dashboardStore';
import { normalizeAnalysisType } from '../stores/systems/keisuke/dashboardStore';
import type { TTestResult } from './statistics/tTest';
import { calculateBasicStatistics } from './statistics/basicStats';
import { buildTTestPaperText } from './adapters/tTestAdapter';
import { buildBasicStatsPaperText } from './adapters/basicStatsAdapter';
import { localizePaperHeadings } from './keisukePaperFormat';

export const PAPER_TEXT_LOCALES = [
  'en',
  'ja',
  'zh-CN',
  'fr',
  'es',
  'de',
  'pt',
  'ru',
] as const;

export type PaperTextLocale = (typeof PAPER_TEXT_LOCALES)[number];

export function isPaperTextLocale(value: string): value is PaperTextLocale {
  return (PAPER_TEXT_LOCALES as readonly string[]).includes(value);
}

export function normalizePaperTextLocale(value: unknown): PaperTextLocale {
  if (typeof value === 'string' && isPaperTextLocale(value)) return value;
  return 'en';
}

export function resolvePaperText(
  resultCache: ResultCache | null | undefined,
  analysisOptions: AnalysisOptions,
  lng: PaperTextLocale
): string | undefined {
  if (!resultCache) return undefined;
  const type = normalizeAnalysisType(analysisOptions.type);
  const raw = resultCache.rawResult;

  if (type === 't-test' && raw && typeof raw === 'object' && 'result' in raw) {
    const r = raw as { result: TTestResult; groupNames?: [string, string] };
    const testType = (analysisOptions.testType as 'unpaired' | 'paired') ?? 'unpaired';
    const mapping = analysisOptions.mapping ?? {};
    const pairedColumns =
      testType === 'paired' && mapping.beforeColumn && mapping.afterColumn
        ? { before: mapping.beforeColumn, after: mapping.afterColumn }
        : undefined;
    return buildTTestPaperText(r.result, testType, r.groupNames, pairedColumns, lng);
  }

  if (type === 'basic-stats' && raw) {
    const col = String(analysisOptions.mapping?.valueColumn ?? resultCache.stats?.column ?? '');
    return buildBasicStatsPaperText(col, raw as ReturnType<typeof calculateBasicStatistics>, lng);
  }

  const english = resultCache.paperText;
  if (!english) return undefined;
  if (lng === 'en') return english;
  return localizePaperHeadings(lng, english);
}
